import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { TREE_FAB_MOTION } from "./treeFabTokens";

// The TreeFab state machine, with no DOM in it.
//
// SPLIT OUT SO IT CAN BE TESTED, which is the whole reason it is a hook rather
// than state inside the component: open/close, select, back, jump, reset and
// the controlled path are decisions, and decisions asserted through a rendered
// FAB are asserted through three animations and a portal.
//
// EVERYTHING HERE IS ADDRESSED BY CODE, never by index. The reference design
// walks the tree with array indices, which works right up until a level is
// reordered or filtered by permission -- then a stored path silently points at
// a different node. Codes are the identity the caller already gave us.

/** Walk a path of codes from the root. Stops at the first code that misses. */
export const nodesAlongPath = (tree, path = []) => {
    // NO TREE IS AN EMPTY CHAIN, not a chain holding nothing. A caller whose
    // tree is still loading renders <TreeFab tree={null}>, and returning
    // [null] here put a null where every reader expects a node -- the hook
    // crashed on node.code before the component's own guard could return.
    if (!tree) return [];
    const chain = [tree];
    let node = tree;
    for (const code of path) {
        const next = (node?.children || []).find((child) => child.code === code);
        // A code that names nothing TRUNCATES rather than throws. A stored or
        // controlled path can outlive the tree it was taken from -- a movement
        // type retired in config, a level hidden by permission -- and dropping
        // the user at the deepest level that still exists is the answer that
        // keeps the control usable. Throwing would blank the page.
        if (!next) break;
        chain.push(next);
        node = next;
    }
    return chain;
};

/** The valid prefix of `path`, which is all of it unless the tree changed. */
export const resolvePath = (tree, path = []) =>
    nodesAlongPath(tree, path)
        .slice(1)
        .map((node) => node.code);

export const nodeAtPath = (tree, path = []) => {
    const chain = nodesAlongPath(tree, path);
    return chain[chain.length - 1];
};

export const isLeafNode = (node) => !(node?.children || []).length;

/** {codes, nodes} for a path, root first. The shape onFinish/onChange take. */
export const selectionFor = (tree, path = []) => {
    const nodes = nodesAlongPath(tree, path);
    return { codes: nodes.map((node) => node.code), nodes };
};

/**
 * @param {Object} options
 * @param {import("./treeFabTokens").TreeFabNode} options.tree
 * @param {string[]} [options.value]        Controlled path, EXCLUDING the root.
 * @param {string[]} [options.defaultValue] Uncontrolled initial path.
 * @param {(sel: any, event: string) => void} [options.onChange]
 * @param {(sel: any) => void} [options.onFinish]
 * @param {"spring"|"calm"} [options.motion]
 * @param {boolean} [options.reducedMotion] Zeroes every wait, which also makes
 *                                          the machine synchronous.
 */
export const useTreeFab = ({
    tree,
    value,
    defaultValue = [],
    onChange,
    onFinish,
    motion = "spring",
    reducedMotion = false,
} = {}) => {
    const timings = reducedMotion
        ? TREE_FAB_MOTION.reduced
        : TREE_FAB_MOTION[motion] || TREE_FAB_MOTION.spring;

    const isControlled = Array.isArray(value);
    const [internalPath, setInternalPath] = useState(() =>
        resolvePath(tree, defaultValue)
    );
    const [open, setOpen] = useState(false);
    const [phase, setPhase] = useState("idle");
    // Which child is shrinking into the FAB, so only that one animates
    // differently from its siblings during a promote.
    const [promotingCode, setPromotingCode] = useState(null);
    const [pulse, setPulse] = useState(false);

    // Re-resolved against the CURRENT tree on every render, not just when the
    // path changes: the tree is data and can change under a standing path --
    // a movement type retired while the menu sits open.
    const path = useMemo(
        () => resolvePath(tree, isControlled ? value : internalPath),
        [tree, isControlled, value, internalPath]
    );

    const nodes = useMemo(() => nodesAlongPath(tree, path), [tree, path]);
    const current = nodes[nodes.length - 1];
    const isLeaf = isLeafNode(current);
    const children = useMemo(() => current?.children || [], [current]);

    const selection = useMemo(() => ({
        codes: nodes.map((node) => node.code),
        nodes,
    }), [nodes]);

    // EVERY pending callback this hook owns -- timeouts AND animation frames --
    // so unmounting mid-transition cannot set state on a dead component or
    // leave `phase` stuck on a promote forever.
    //
    // THE FRAMES WERE MISSING. reopenAt schedules a raf(raf(setOpen)) pair and
    // neither handle was stored, so the cleanup below cleared timeouts only
    // while the comment claimed it cleared everything. That path is reachable
    // in ordinary use: the module unmounts this component the moment a row is
    // ticked, and onFinish navigates away mid-walk.
    const timers = useRef([]);
    const frames = useRef([]);
    const clearPending = useCallback(() => {
        timers.current.forEach(clearTimeout);
        timers.current = [];
        frames.current.forEach(cancelAnimationFrame);
        frames.current = [];
    }, []);
    useEffect(() => clearPending, [clearPending]);

    // ZERO IS SYNCHRONOUS, not a zero-delay timeout. Under reduced motion the
    // whole machine then runs inside the click, which is what lets the tests
    // read the result without advancing a clock.
    //
    // A SETTLED HANDLE IS DROPPED rather than left in the array. Pushing and
    // never splicing turned these into a list that grew for the life of the
    // session and made every cleanup O(total clicks).
    const after = useCallback((delay, run) => {
        if (!delay) {
            run();
            return;
        }
        const id = setTimeout(() => {
            timers.current = timers.current.filter((pending) => pending !== id);
            run();
        }, delay);
        timers.current.push(id);
    }, []);

    const nextFrame = useCallback((run) => {
        const id = requestAnimationFrame(() => {
            frames.current = frames.current.filter((pending) => pending !== id);
            run();
        });
        frames.current.push(id);
    }, []);

    const report = useCallback(
        (nextPath, event) => {
            if (!onChange) return;
            onChange(selectionFor(tree, nextPath), event);
        },
        [onChange, tree]
    );

    // The path is only written HERE when uncontrolled. A controlled caller
    // gets onChange and decides; writing internal state as well would make the
    // two disagree the moment the caller declined a change.
    const commitPath = useCallback(
        (nextPath) => {
            if (!isControlled) setInternalPath(nextPath);
        },
        [isControlled]
    );

    const firePulse = useCallback(() => {
        if (!timings.pulseDuration) return;
        setPulse(true);
        after(timings.pulseDuration, () => setPulse(false));
    }, [timings.pulseDuration, after]);

    // Collapse, change the path, then burst out again on the next frame.
    //
    // TWO FRAMES, not one. The children have to be MOUNTED at their collapsed
    // transform before the transform that moves them out is applied, or the
    // browser coalesces both into the final state and there is no animation at
    // all -- the reference design's `raf(raf(...))` for the same reason.
    const reopenAt = useCallback(
        (nextPath) => {
            setOpen(false);
            setPhase("idle");
            setPromotingCode(null);
            commitPath(nextPath);
            if (!timings.openDuration) {
                setOpen(true);
                return;
            }
            nextFrame(() => nextFrame(() => setOpen(true)));
        },
        [commitPath, timings.openDuration, nextFrame]
    );

    // Leave the current level, land on another. Reopens when the destination
    // has children; a leaf has nothing to burst out.
    const swapTo = useCallback(
        (nextPath, { reopen = true } = {}) => {
            setOpen(false);
            setPhase("swap");
            after(timings.swapDuration, () => {
                const destination = nodeAtPath(tree, nextPath);
                if (reopen && !isLeafNode(destination)) {
                    reopenAt(nextPath);
                    return;
                }
                commitPath(nextPath);
                setPhase("idle");
                setOpen(false);
            });
        },
        [after, timings.swapDuration, tree, reopenAt, commitPath]
    );

    // ONE TRANSITION AT A TIME. A second click mid-animation would start a
    // path change from a level that is already leaving, and the two would race
    // to write `path`.
    const isBusy = phase !== "idle";

    const toggleRoot = useCallback(() => {
        if (isBusy) return;
        // On a leaf the FAB is the finish state's tick, not a menu button:
        // there is nothing under it to open.
        if (isLeaf) return;
        firePulse();
        // REPORTED OUTSIDE THE UPDATER. `report` calls the consumer's
        // onChange, and React does not promise an updater runs exactly once --
        // it replays them under StrictMode and may re-invoke them while
        // rendering concurrently. `open` is already in this closure, so there
        // was never a reason to read it from the updater.
        report(path, open ? "close" : "open");
        setOpen(!open);
    }, [isBusy, isLeaf, firePulse, report, path, open]);

    const close = useCallback(() => {
        if (isBusy || !open) return;
        setOpen(false);
        report(path, "close");
    }, [isBusy, open, report, path]);

    const selectChild = useCallback(
        (code) => {
            if (isBusy) return;
            const child = children.find((node) => node.code === code);
            if (!child || child.disabled) return;

            const nextPath = [...path, code];
            setPromotingCode(code);
            setPhase("promote");
            after(timings.promoteDuration, () => {
                firePulse();
                report(nextPath, "select");
                if (!isLeafNode(child)) {
                    reopenAt(nextPath);
                    return;
                }
                // A leaf ends the walk: the children are gone and the finish
                // card takes their place, so there is nothing to reopen.
                commitPath(nextPath);
                setPhase("idle");
                setPromotingCode(null);
                setOpen(false);
            });
        },
        [
            isBusy, children, path, after, timings.promoteDuration,
            firePulse, report, reopenAt, commitPath,
        ]
    );

    const back = useCallback(() => {
        if (isBusy) return;
        // At the root there is no level to leave, so Back closes -- which is
        // what Escape means on an open menu with nothing selected.
        if (!path.length) {
            if (open) {
                setOpen(false);
                report(path, "close");
            }
            return;
        }
        firePulse();
        const nextPath = path.slice(0, -1);
        report(nextPath, "back");
        swapTo(nextPath);
    }, [isBusy, path, open, firePulse, report, swapTo]);


    const reset = useCallback(() => {
        if (isBusy) return;
        firePulse();
        report([], "reset");
        // reopen:false -- Reset leaves ONLY the FAB, which is the state the
        // page started in. Reopening would clear the path and then immediately
        // show a menu nobody asked for.
        swapTo([], { reopen: false });
    }, [isBusy, firePulse, report, swapTo]);

    const finish = useCallback(() => {
        if (isBusy || !isLeaf) return;
        firePulse();
        onFinish?.(selectionFor(tree, path));
        // BACK TO THE START after handing over. The caller has taken the
        // selection somewhere -- a form, a dialog -- and a FAB still showing a
        // green tick for a walk that is over invites a second submit.
        commitPath([]);
        setOpen(false);
        report([], "reset");
    }, [isBusy, isLeaf, firePulse, onFinish, tree, path, commitPath, report]);

    return {
        // state
        path,
        nodes,
        current,
        children,
        selection,
        isLeaf,
        isOpen: open,
        phase,
        isBusy,
        promotingCode,
        pulse,
        timings,
        depth: path.length,
        // actions
        toggleRoot,
        close,
        selectChild,
        back,
        reset,
        finish,
    };
};

export default useTreeFab;
